import React from 'react';
import { Box, Tab, Tooltip, Typography } from '@mui/material';
import TabList from '@mui/lab/TabList';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { alpha } from '@mui/material/styles';
import { selectedHeaderPalette } from 'src/theme/adminActionButton';
import { QuoteEventIdentity, formatQuoteIdentityDates, formatQuoteIdentityLocation } from 'src/content/applications/quote/quoteEventIdentity';

export const contractPhaseNames: Record<string, string> = {
  dashboard: 'dashboard', '0': 'quote', '2': 'bookings', '4': 'attendance', '5': 'actuals',
  '6': 'worker-invoice', '7': 'client-invoice', '10': 'financial-statement', '11': 'ratings'
};

interface Props {
  value: string;
  id?: string;
  eventName?: string;
  eventIdentity?: QuoteEventIdentity;
  contractManagers?: string[];
  isNewQuoteWorkspace?: boolean;
  disabled?: boolean;
  actionsRef?: React.Ref<HTMLDivElement>;
  onBack: () => void;
  onChange: (event: React.SyntheticEvent, value: string) => void;
  onReselect?: (value: string) => void;
}

/** Existing contract navigation shared by the event workspace and its generated documents. */
export default function EventContractNavigation({
  value, eventName, eventIdentity, contractManagers, isNewQuoteWorkspace = false,
  disabled = false, actionsRef, onBack, onChange, onReselect
}: Props) {
  const identity = eventIdentity || { name: eventName };
  return (
        <Box
          data-testid="event-contract-navigation"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            backgroundColor: 'white',
            pt: 0
            // borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Box data-testid="event-contract-identity" sx={{ display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)' },
            alignItems: 'center', columnGap: 2, rowGap: 0.25,
            px: { xs: 2, sm: 3, md: 6 }, py: 0.5,
            borderBottom: theme => `1px solid ${alpha(theme.palette.text.primary, 0.05)}` }}>
            <Typography component="p" variant="caption" color="text.secondary" sx={{ m: 0, minWidth: 0, textTransform: 'none', overflowWrap: 'anywhere', order: { xs: 2, sm: 1 } }}>{formatQuoteIdentityDates(identity)}</Typography>
            <Box sx={{ minWidth: 0, textAlign: 'center', order: { xs: 1, sm: 2 } }}>
              <Tooltip title={contractManagers?.length ? `Contract Manager: ${contractManagers.join(', ')}` : ''} describeChild arrow>
                <Typography component="h1" variant="h4" tabIndex={contractManagers?.length ? 0 : undefined} sx={{ m: 0, overflowWrap: 'anywhere' }}>
                  {identity.name || eventName || 'Untitled Event'}
                </Typography>
              </Tooltip>
            </Box>
            <Typography component="p" variant="caption" color="text.secondary" sx={{ m: 0, minWidth: 0, textTransform: 'none', overflowWrap: 'anywhere', textAlign: { xs: 'left', sm: 'right' }, order: 3 }}>{formatQuoteIdentityLocation(identity)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: { xs: 2, sm: 3, md: 6 }, pt: '1px', pb: 0 }}>
            <Box component="a" href="/dashboards/jobTracker"
              aria-label="Back To Contracts"
              onClick={(event) => { event.preventDefault(); if (!disabled) onBack(); }}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : undefined}
              sx={{ display: 'flex', alignItems: 'center', height: 36, color: 'inherit', flexShrink: 0 }}>
              <KeyboardBackspaceIcon />
            </Box>
            <TabList
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flex: '0 1 auto', minWidth: 0, minHeight: 36,
                '& .MuiTab-root': { minWidth: 0, minHeight: 36, px: 1.25, py: '7px', textTransform: 'none', typography: 'body1', border: '1px solid transparent',
                  '&.Mui-selected': { color: 'text.primary', backgroundColor: selectedHeaderPalette.text,
                    borderColor: selectedHeaderPalette.border, borderBottomColor: selectedHeaderPalette.text,
                    borderRadius: '8px 8px 0 0' } },
                '& .MuiTabs-indicator': { display: 'none' }
              }}
              onChange={onChange}
              aria-label="Contract Phases"
            >
              <Tab
                sx={{
                  color: value === 'dashboard' ? '#F7A408' : 'gray',
                  fontSize: 12
                }}
                label="Dashboard"
                value="dashboard"
                disabled={disabled}
                onClick={() => { if (value === 'dashboard') onReselect?.('dashboard'); }}
              />
              <Tab
                sx={{ color: value === '0' ? '#F7A408' : 'gray', fontSize: 12 }}
                id={isNewQuoteWorkspace ? "new-quote-tab" : undefined}
                aria-controls={isNewQuoteWorkspace ? "new-quote-panel" : undefined}
                label="Quote"
                value="0"
                disabled={disabled}
                onClick={() => { if (value === '0') onReselect?.('0'); }}
              />
              <Tab
                sx={{ color: value === '2' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Bookings"
                value="2"
                disabled={disabled}
                onClick={() => { if (value === '2') onReselect?.('2'); }}
              />
              <Tab
                sx={{ color: value === '4' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Attendance"
                value="4"
                disabled={disabled}
                onClick={() => { if (value === '4') onReselect?.('4'); }}
              />
              <Tab
                sx={{ color: value === '5' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Actuals"
                value="5"
                disabled={disabled || isNewQuoteWorkspace}
                onClick={() => { if (value === '5') onReselect?.('5'); }}
              />
              <Tab
                sx={{ color: value === '6' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Worker Invoice"
                value="6"
                disabled={disabled}
                onClick={() => { if (value === '6') onReselect?.('6'); }}
              />
              <Tab
                sx={{ color: value === '7' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Client Invoice"
                value="7"
                disabled={disabled}
                onClick={() => { if (value === '7') onReselect?.('7'); }}
              />

              {/* <Tab
                sx={{ color: value === '9' ? '#F7A408' : 'gray', fontSize: 12 }}
                label="Payroll"
                value="9"
              /> */}
              <Tab
                sx={{
                  color: value === '10' ? '#F7A408' : 'gray',
                  fontSize: 12
                }}
                label="Financial Statement"
                value="10"
                disabled={disabled}
                onClick={() => { if (value === '10') onReselect?.('10'); }}
              />

            </TabList>
            <Box ref={actionsRef} sx={{ flexShrink: 0, ml: 'auto', display: value === '0' || value === '2' ? 'block' : 'none', '& button': { whiteSpace: 'nowrap' } }} />
          </Box>
        </Box>
  );
}
